// Outbound links & calendar files. The app never claims it "sent" something: it opens drafts
// in the advisor's own apps (VÉRO offline rule since v320).
import type { Task } from '@vero/domain';

export const telLink = (phone?: string | null) => (phone ? `tel:${phone.replace(/[^\d+]/g, '')}` : undefined);
export const smsLink = (phone?: string | null, body?: string) => (phone ? `sms:${phone.replace(/[^\d+]/g, '')}${body ? `?&body=${encodeURIComponent(body)}` : ''}` : undefined);
export const mailLink = (email?: string | null, subject?: string, body?: string) =>
  email ? `mailto:${email}?${new URLSearchParams({ ...(subject ? { subject } : {}), ...(body ? { body } : {}) }).toString().replace(/\+/g, '%20')}` : undefined;
export function waLink(phone?: string | null, text?: string, defaultPrefix = '357') {
  if (!phone) return undefined;
  let d = phone.replace(/\D/g, '').replace(/^00/, '');
  if (d.length === 8) d = defaultPrefix + d; // local CY/GR short numbers
  return `https://wa.me/${d}${text ? `?text=${encodeURIComponent(text)}` : ''}`;
}
export const mapsLink = (address?: string | null, url?: string | null) => url || (address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}` : undefined);

function icsDate(day: string, time: string, addMin = 0): string {
  const [y, m, d] = day.split('-').map(Number);
  const [h, mi] = time.split(':').map(Number);
  const dt = new Date(y!, m! - 1, d!, h!, mi! + addMin);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${dt.getFullYear()}${p(dt.getMonth() + 1)}${p(dt.getDate())}T${p(dt.getHours())}${p(dt.getMinutes())}00`;
}
const esc = (s: string) => s.replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\n/g, '\\n');

export function icsFor(t: Pick<Task, 'id' | 'title' | 'dueDate' | 'dueTime' | 'durationMin' | 'location' | 'notes'>, organizer?: string): string | null {
  if (!t.dueDate) return null;
  const start = icsDate(t.dueDate, t.dueTime || '09:00');
  const end = icsDate(t.dueDate, t.dueTime || '09:00', t.durationMin || 45);
  const lines = [
    'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//VERO OS//Advisor OS//EN', 'CALSCALE:GREGORIAN', 'METHOD:PUBLISH', 'BEGIN:VEVENT',
    `UID:${t.id}@vero-os`, `DTSTAMP:${new Date().toISOString().replace(/[-:]/g, '').replace(/\.\d+Z$/, 'Z')}`,
    `DTSTART:${start}`, `DTEND:${end}`, `SUMMARY:${esc(t.title)}`,
    ...(t.location ? [`LOCATION:${esc(t.location)}`] : []), ...(t.notes ? [`DESCRIPTION:${esc(t.notes)}`] : []),
    ...(organizer ? [`ORGANIZER;CN=${esc(organizer)}:mailto:noreply@vero.local`] : []),
    'BEGIN:VALARM', 'TRIGGER:-PT30M', 'ACTION:DISPLAY', `DESCRIPTION:${esc(t.title)}`, 'END:VALARM', 'END:VEVENT', 'END:VCALENDAR',
  ];
  return lines.join('\r\n');
}
export function googleCalendarLink(t: Pick<Task, 'title' | 'dueDate' | 'dueTime' | 'durationMin' | 'location' | 'notes'>): string | undefined {
  if (!t.dueDate) return undefined;
  const q = new URLSearchParams({ action: 'TEMPLATE', text: t.title, dates: `${icsDate(t.dueDate, t.dueTime || '09:00')}/${icsDate(t.dueDate, t.dueTime || '09:00', t.durationMin || 45)}`, details: t.notes ?? '', location: t.location ?? '' });
  return `https://calendar.google.com/calendar/render?${q}`;
}

export function download(filename: string, content: string | Blob, type = 'application/json') {
  const blob = content instanceof Blob ? content : new Blob([content], { type });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 1500);
}

/** Open a web page outside the app: the system browser on desktop (guarded native command), a new tab on the web. */
export async function openExternal(url: string): Promise<void> {
  const w = window as unknown as { __TAURI__?: { core?: { invoke: (cmd: string, args?: Record<string, unknown>) => Promise<unknown> } } };
  const inv = w.__TAURI__?.core?.invoke;
  if (inv) { await inv('open_external', { url }); return; }
  window.open(url, '_blank', 'noopener');
}
